import React from 'react';
import PageHeader from '../components/layout/PageHeader';
import AdmissionForm from '../components/admissions/AdmissionForm';
import { CalendarIcon, FileTextIcon, CheckSquareIcon, CreditCardIcon } from 'lucide-react';
import Tabs from '../components/ui/Tabs';
import Alert from '../components/ui/Alert';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
const Admissions = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  // Assuming academic year starts in September (month 8)
  const isAdmissionOpen = currentMonth >= 3 && currentMonth <= 7; // April to August
  return <div className="min-h-screen bg-white" data-id="element-2534">
      <PageHeader title="Admissions" description="Join our community of learners and future leaders. Discover the admission process, requirements, and how to apply to Isonyin Grammar School." backgroundImage="https://uploadthingy.s3.us-west-1.amazonaws.com/guWk5XsDGYmXXcoEtCyUpd/WhatsApp_Image_2025-10-24_at_11.30.12_%281%29.jpg" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Admissions',
      path: '/admissions'
    }]} buttons={<>
            <Button href="#apply" variant="primary" size="lg" animation="scale" data-id="element-2536">
              Apply Now
            </Button>
            <Button href="#requirements" variant="outline" size="lg" className="bg-blue-800/50 border-white text-white hover:bg-blue-700" data-id="element-2537">
              View Requirements
            </Button>
          </>} data-id="element-2535" />
      <div className="container mx-auto px-4 py-12" data-id="element-2538">
        {/* Admission Status Alert */}
        {isAdmissionOpen ? <Alert variant="success" title="Admissions Open for 2023/2024 Academic Session" className="mb-8" data-id="element-2539">
            We are currently accepting applications for the 2023/2024 academic
            session. The deadline for submission is August 15, 2023.
          </Alert> : <Alert variant="info" title="Admissions for 2023/2024 Academic Session" className="mb-8" data-id="element-2540">
            Admissions for the 2023/2024 academic session will open in April
            2023. You can submit a pre-application interest form below to be
            notified when admissions open.
          </Alert>}
        {/* Admission Process Section */}
        <section className="mb-16" data-id="element-2541">
          <div className="text-center mb-10" data-id="element-2542">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2543">
              Admission Process
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2544"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2545">
              Our admission process is designed to identify students who will
              thrive in our challenging academic environment and contribute
              positively to our school community.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-2546">
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 hover:shadow-md transition-shadow text-center" data-id="element-2547">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-900 text-white rounded-full mb-4" data-id="element-2548">
                <FileTextIcon className="h-8 w-8" data-id="element-2549" />
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-2550">
                1. Application
              </h3>
              <p className="text-gray-600" data-id="element-2551">
                Complete and submit the application form with all required
                documents and application fee.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 hover:shadow-md transition-shadow text-center" data-id="element-2552">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-900 text-white rounded-full mb-4" data-id="element-2553">
                <CheckSquareIcon className="h-8 w-8" data-id="element-2554" />
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-2555">
                2. Entrance Exam
              </h3>
              <p className="text-gray-600" data-id="element-2556">
                Qualified applicants will be invited to take an entrance
                examination to assess academic readiness.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 hover:shadow-md transition-shadow text-center" data-id="element-2557">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-900 text-white rounded-full mb-4" data-id="element-2558">
                <CalendarIcon className="h-8 w-8" data-id="element-2559" />
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-2560">
                3. Interview
              </h3>
              <p className="text-gray-600" data-id="element-2561">
                Selected candidates and their parents will be invited for an
                interview with our admissions committee.
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 hover:shadow-md transition-shadow text-center" data-id="element-2562">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-900 text-white rounded-full mb-4" data-id="element-2563">
                <CreditCardIcon className="h-8 w-8" data-id="element-2564" />
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-2565">
                4. Acceptance
              </h3>
              <p className="text-gray-600" data-id="element-2566">
                Successful candidates will receive an offer letter and proceed
                with the enrollment process.
              </p>
            </div>
          </div>
        </section>
        {/* Tabs Section */}
        <section className="mb-16" id="requirements" data-id="element-2567">
          <Tabs tabs={[{
          id: 'requirements',
          label: 'Requirements',
          content: <div className="bg-white p-6 rounded-lg shadow" data-id="element-2569">
                    <h3 className="text-xl font-bold text-blue-900 mb-4" data-id="element-2570">
                      Admission Requirements
                    </h3>
                    <div className="mb-6" data-id="element-2571">
                      <h4 className="font-bold text-blue-800 mb-2" data-id="element-2572">
                        Required Documents
                      </h4>
                      <ul className="list-disc list-inside space-y-2 text-gray-700" data-id="element-2573">
                        <li data-id="element-2574">Completed application form</li>
                        <li data-id="element-2575">
                          Birth certificate or passport (for age verification)
                        </li>
                        <li data-id="element-2576">Two passport-sized photographs</li>
                        <li data-id="element-2577">
                          Academic records/transcripts from previous school
                        </li>
                        <li data-id="element-2578">Letter of recommendation from previous school</li>
                        <li data-id="element-2579">Immunization records</li>
                        <li data-id="element-2580">Application fee receipt</li>
                      </ul>
                    </div>
                    <div className="mb-6" data-id="element-2581">
                      <h4 className="font-bold text-blue-800 mb-2" data-id="element-2582">
                        Age Requirements
                      </h4>
                      <p className="text-gray-700" data-id="element-2583">
                        Students entering JSS1 must be between 10-12 years of
                        age by September 1st of the academic year. Transfer
                        students to other classes must meet the corresponding
                        age requirements.
                      </p>
                    </div>
                    <div data-id="element-2584">
                      <h4 className="font-bold text-blue-800 mb-2" data-id="element-2585">
                        Academic Requirements
                      </h4>
                      <p className="text-gray-700 mb-2" data-id="element-2586">
                        Prospective students must demonstrate academic readiness
                        through:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-gray-700" data-id="element-2587">
                        <li data-id="element-2588">
                          Satisfactory performance in the entrance examination
                        </li>
                        <li data-id="element-2589">Good academic standing from previous school</li>
                        <li data-id="element-2590">
                          Passing grades in core subjects (English, Mathematics,
                          Science, and Social Studies)
                        </li>
                      </ul>
                    </div>
                  </div>
        }, {
          id: 'fees',
          label: 'Tuition & Fees',
          content: <div className="bg-white p-6 rounded-lg shadow" data-id="element-2591">
                    <h3 className="text-xl font-bold text-blue-900 mb-4" data-id="element-2592">
                      Tuition and Fees for 2023/2024
                    </h3>
                    <div className="overflow-x-auto" data-id="element-2593">
                      <table className="min-w-full bg-white border border-gray-200" data-id="element-2594">
                        <thead data-id="element-2595">
                          <tr className="bg-blue-900 text-white" data-id="element-2596">
                            <th className="py-3 px-4 text-left" data-id="element-2597">Fee Type</th>
                            <th className="py-3 px-4 text-left" data-id="element-2598">Day Student</th>
                            <th className="py-3 px-4 text-left" data-id="element-2599">
                              Boarding Student
                            </th>
                            <th className="py-3 px-4 text-left" data-id="element-2600">
                              Payment Schedule
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200" data-id="element-2601">
                          <tr data-id="element-2602">
                            <td className="py-3 px-4 font-medium" data-id="element-2603">
                              Application Fee
                            </td>
                            <td className="py-3 px-4" data-id="element-2604">₦5,000</td>
                            <td className="py-3 px-4" data-id="element-2605">₦5,000</td>
                            <td className="py-3 px-4" data-id="element-2606">One-time</td>
                          </tr>
                          <tr className="bg-gray-50" data-id="element-2607">
                            <td className="py-3 px-4 font-medium" data-id="element-2608">
                              Tuition Fee
                            </td>
                            <td className="py-3 px-4" data-id="element-2609">₦150,000</td>
                            <td className="py-3 px-4" data-id="element-2610">₦150,000</td>
                            <td className="py-3 px-4" data-id="element-2611">Per term</td>
                          </tr>
                          <tr data-id="element-2612">
                            <td className="py-3 px-4 font-medium" data-id="element-2613">
                              Boarding Fee
                            </td>
                            <td className="py-3 px-4" data-id="element-2614">N/A</td>
                            <td className="py-3 px-4" data-id="element-2615">₦120,000</td>
                            <td className="py-3 px-4" data-id="element-2616">Per term</td>
                          </tr>
                          <tr className="bg-gray-50" data-id="element-2617">
                            <td className="py-3 px-4 font-medium" data-id="element-2618">
                              Development Levy
                            </td>
                            <td className="py-3 px-4" data-id="element-2619">₦25,000</td>
                            <td className="py-3 px-4" data-id="element-2620">₦25,000</td>
                            <td className="py-3 px-4" data-id="element-2621">Annual</td>
                          </tr>
                          <tr data-id="element-2622">
                            <td className="py-3 px-4 font-medium" data-id="element-2623">
                              Books & Materials
                            </td>
                            <td className="py-3 px-4" data-id="element-2624">₦40,000</td>
                            <td className="py-3 px-4" data-id="element-2625">₦40,000</td>
                            <td className="py-3 px-4" data-id="element-2626">Annual</td>
                          </tr>
                          <tr className="bg-gray-50" data-id="element-2627">
                            <td className="py-3 px-4 font-medium" data-id="element-2628">Uniform</td>
                            <td className="py-3 px-4" data-id="element-2629">₦35,000</td>
                            <td className="py-3 px-4" data-id="element-2630">₦50,000</td>
                            <td className="py-3 px-4" data-id="element-2631">One-time</td>
                          </tr>
                          <tr data-id="element-2632">
                            <td className="py-3 px-4 font-medium" data-id="element-2633">
                              ICT & Lab Fee
                            </td>
                            <td className="py-3 px-4" data-id="element-2634">₦15,000</td>
                            <td className="py-3 px-4" data-id="element-2635">₦15,000</td>
                            <td className="py-3 px-4" data-id="element-2636">Per term</td>
                          </tr>
                          <tr className="bg-gray-50" data-id="element-2637">
                            <td className="py-3 px-4 font-medium" data-id="element-2638">
                              Sports Fee
                            </td>
                            <td className="py-3 px-4" data-id="element-2639">₦10,000</td>
                            <td className="py-3 px-4" data-id="element-2640">₦10,000</td>
                            <td className="py-3 px-4" data-id="element-2641">Per term</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-6 space-y-4 text-gray-700" data-id="element-2642">
                      <p data-id="element-2643">
                        <strong data-id="element-2644">Note:</strong> All fees are subject to review. A
                        5% discount is offered for full annual payment.
                      </p>
                      <h4 className="font-bold text-blue-800" data-id="element-2645">
                        Payment Methods
                      </h4>
                      <p data-id="element-2646">Fees can be paid through:</p>
                      <ul className="list-disc list-inside space-y-1" data-id="element-2647">
                        <li data-id="element-2648">Bank transfer to our school account</li>
                        <li data-id="element-2649">
                          Direct deposit at any branch of First Bank or Zenith
                          Bank
                        </li>
                        <li data-id="element-2650">
                          Online payment through our school portal (coming soon)
                        </li>
                      </ul>
                      <p data-id="element-2651">
                        For detailed information about our payment policies,
                        please contact our finance office.
                      </p>
                    </div>
                  </div>
        }, {
          id: 'faqs',
          label: 'FAQs',
          content: <div className="bg-white p-6 rounded-lg shadow" data-id="element-2652">
                    <h3 className="text-xl font-bold text-blue-900 mb-4" data-id="element-2653">
                      Frequently Asked Questions
                    </h3>
                    <div className="space-y-6" data-id="element-2654">
                      <div data-id="element-2655">
                        <h4 className="font-bold text-blue-800 mb-2" data-id="element-2656">
                          When can I apply for admission?
                        </h4>
                        <p className="text-gray-700" data-id="element-2657">
                          Admissions for the new academic year typically open in
                          April and close in August. However, we accept transfer
                          applications throughout the year, subject to
                          availability.
                        </p>
                      </div>
                      <div data-id="element-2658">
                        <h4 className="font-bold text-blue-800 mb-2" data-id="element-2659">
                          What is the class size at Isonyin Grammar School?
                        </h4>
                        <p className="text-gray-700" data-id="element-2660">
                          We maintain a maximum class size of 30 students to
                          ensure quality education and individual attention.
                        </p>
                      </div>
                      <div data-id="element-2661">
                        <h4 className="font-bold text-blue-800 mb-2" data-id="element-2662">
                          Do you offer scholarships?
                        </h4>
                        <p className="text-gray-700" data-id="element-2663">
                          Yes, we offer merit-based and need-based scholarships.
                          Qualified candidates can apply through our scholarship
                          program. Visit our{' '}
                          <a href="/scholarships" className="text-blue-600 hover:underline" data-id="element-2664">
                            Scholarships page
                          </a>{' '}
                          for more information.
                        </p>
                      </div>
                      <div data-id="element-2665">
                        <h4 className="font-bold text-blue-800 mb-2" data-id="element-2666">
                          What is the admission success rate?
                        </h4>
                        <p className="text-gray-700" data-id="element-2667">
                          Our admission process is competitive, with an
                          acceptance rate of approximately 60%. We evaluate each
                          applicant holistically, considering academic
                          performance, character, and potential.
                        </p>
                      </div>
                      <div data-id="element-2668">
                        <h4 className="font-bold text-blue-800 mb-2" data-id="element-2669">
                          Can international students apply?
                        </h4>
                        <p className="text-gray-700" data-id="element-2670">
                          Yes, we welcome international students. Additional
                          documentation such as visa status and passport
                          information will be required during the application
                          process.
                        </p>
                      </div>
                      <div data-id="element-2671">
                        <h4 className="font-bold text-blue-800 mb-2" data-id="element-2672">
                          Is there a waiting list?
                        </h4>
                        <p className="text-gray-700" data-id="element-2673">
                          Yes, qualified candidates who cannot be immediately
                          admitted due to space constraints may be placed on a
                          waiting list and will be contacted if a spot becomes
                          available.
                        </p>
                      </div>
                      <div data-id="element-2674">
                        <h4 className="font-bold text-blue-800 mb-2" data-id="element-2675">
                          How can I schedule a school tour?
                        </h4>
                        <p className="text-gray-700" data-id="element-2676">
                          You can schedule a school tour by contacting our
                          admissions office at
                          <a href="mailto:admissions@isonyingrammarschool.edu.ng" className="text-blue-600 hover:underline" data-id="element-2677">
                            {' '}
                            admissions@isonyingrammarschool.edu.ng
                          </a>{' '}
                          or calling{' '}
                          <a href="tel:+2348031234567" className="text-blue-600 hover:underline" data-id="element-2678">
                            +234 (0) 803 123 4567
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </div>
        }, {
          id: 'calendar',
          label: <div className="flex items-center" data-id="element-2679">
                    Admission Calendar
                    <Badge variant="primary" size="sm" rounded className="ml-2" data-id="element-2680">
                      New
                    </Badge>
                  </div>,
          content: <div className="bg-white p-6 rounded-lg shadow" data-id="element-2681">
                    <h3 className="text-xl font-bold text-blue-900 mb-4" data-id="element-2682">
                      Admission Calendar for 2023/2024
                    </h3>
                    <div className="overflow-x-auto" data-id="element-2683">
                      <table className="min-w-full bg-white border border-gray-200" data-id="element-2684">
                        <thead data-id="element-2685">
                          <tr className="bg-blue-900 text-white" data-id="element-2686">
                            <th className="py-3 px-4 text-left" data-id="element-2687">Event</th>
                            <th className="py-3 px-4 text-left" data-id="element-2688">Date</th>
                            <th className="py-3 px-4 text-left" data-id="element-2689">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200" data-id="element-2690">
                          <tr data-id="element-2691">
                            <td className="py-3 px-4 font-medium" data-id="element-2692">
                              Application Portal Opens
                            </td>
                            <td className="py-3 px-4" data-id="element-2693">April 1, 2023</td>
                            <td className="py-3 px-4" data-id="element-2694">
                              Online and physical applications begin
                            </td>
                          </tr>
                          <tr className="bg-gray-50" data-id="element-2695">
                            <td className="py-3 px-4 font-medium" data-id="element-2696">
                              Open House
                            </td>
                            <td className="py-3 px-4" data-id="element-2697">April 15, 2023</td>
                            <td className="py-3 px-4" data-id="element-2698">
                              School tour and information session for
                              prospective families
                            </td>
                          </tr>
                          <tr data-id="element-2699">
                            <td className="py-3 px-4 font-medium" data-id="element-2700">
                              Application Deadline
                            </td>
                            <td className="py-3 px-4" data-id="element-2701">August 15, 2023</td>
                            <td className="py-3 px-4" data-id="element-2702">
                              All applications must be submitted by this date
                            </td>
                          </tr>
                          <tr className="bg-gray-50" data-id="element-2703">
                            <td className="py-3 px-4 font-medium" data-id="element-2704">
                              Entrance Examination
                            </td>
                            <td className="py-3 px-4" data-id="element-2705">August 26, 2023</td>
                            <td className="py-3 px-4" data-id="element-2706">
                              8:00 AM at the school premises
                            </td>
                          </tr>
                          <tr data-id="element-2707">
                            <td className="py-3 px-4 font-medium" data-id="element-2708">
                              Interviews
                            </td>
                            <td className="py-3 px-4" data-id="element-2709">September 4-8, 2023</td>
                            <td className="py-3 px-4" data-id="element-2710">By appointment only</td>
                          </tr>
                          <tr className="bg-gray-50" data-id="element-2711">
                            <td className="py-3 px-4 font-medium" data-id="element-2712">
                              Admission Decisions
                            </td>
                            <td className="py-3 px-4" data-id="element-2713">September 15, 2023</td>
                            <td className="py-3 px-4" data-id="element-2714">
                              Results will be communicated via email and SMS
                            </td>
                          </tr>
                          <tr data-id="element-2715">
                            <td className="py-3 px-4 font-medium" data-id="element-2716">
                              Acceptance Deadline
                            </td>
                            <td className="py-3 px-4" data-id="element-2717">September 30, 2023</td>
                            <td className="py-3 px-4" data-id="element-2718">
                              Accepted students must confirm enrollment by this
                              date
                            </td>
                          </tr>
                          <tr className="bg-gray-50" data-id="element-2719">
                            <td className="py-3 px-4 font-medium" data-id="element-2720">
                              New Student Orientation
                            </td>
                            <td className="py-3 px-4" data-id="element-2721">October 7, 2023</td>
                            <td className="py-3 px-4" data-id="element-2722">
                              Mandatory for all new students and their parents
                            </td>
                          </tr>
                          <tr data-id="element-2723">
                            <td className="py-3 px-4 font-medium" data-id="element-2724">
                              First Day of School
                            </td>
                            <td className="py-3 px-4" data-id="element-2725">October 9, 2023</td>
                            <td className="py-3 px-4" data-id="element-2726">
                              Beginning of 2023/2024 academic session
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-6" data-id="element-2727">
                      <p className="text-gray-700" data-id="element-2728">
                        <strong data-id="element-2729">Note:</strong> Dates are subject to change.
                        Please check our website regularly for updates.
                      </p>
                    </div>
                  </div>
        }]} variant="boxed" className="mb-8" data-id="element-2568" />
        </section>
        {/* Application Form Section */}
        <section className="mb-16" id="apply" data-id="element-2730">
          <div className="text-center mb-10" data-id="element-2731">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2732">Apply Now</h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2733"></div>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto" data-id="element-2734">
              Take the first step towards joining Isonyin Grammar School by
              submitting your application.
            </p>
          </div>
          <div className="max-w-3xl mx-auto" data-id="element-2735">
            <AdmissionForm data-id="element-2736" />
          </div>
        </section>
        {/* Testimonials Section */}
        <section data-id="element-2737">
          <div className="text-center mb-10" data-id="element-2738">
            <h2 className="text-3xl font-bold text-blue-900 mb-3" data-id="element-2739">
              What Our Students Say
            </h2>
            <div className="w-24 h-1 bg-yellow-500 mx-auto" data-id="element-2740"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2741">
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 relative" data-id="element-2742">
              <div className="absolute -top-4 -left-4 text-6xl text-yellow-500 opacity-50" data-id="element-2743">
                "
              </div>
              <div className="relative z-10" data-id="element-2744">
                <p className="text-gray-700 italic mb-4" data-id="element-2745">
                  The admission process was straightforward and the staff was
                  incredibly helpful. They guided us through every step and made
                  us feel welcome. I'm so grateful my son is now part of this
                  amazing school.
                </p>
                <div className="flex items-center" data-id="element-2746">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3iuYwPBfNvbAqWiL5UGdQD/WhatsApp_Image_2025-08-14_at_15.45.35_%283%29.jpg" alt="Parent" className="w-12 h-12 rounded-full object-cover mr-4" data-id="element-2747" />
                  <div data-id="element-2748">
                    <h4 className="font-bold text-blue-900" data-id="element-2749">
                      Mrs. Adebola Johnson
                    </h4>
                    <p className="text-sm text-gray-500" data-id="element-2750">
                      Parent of JSS1 Student
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 relative" data-id="element-2751">
              <div className="absolute -top-4 -left-4 text-6xl text-yellow-500 opacity-50" data-id="element-2752">
                "
              </div>
              <div className="relative z-10" data-id="element-2753">
                <p className="text-gray-700 italic mb-4" data-id="element-2754">
                  I was nervous about the entrance exam, but the teachers made
                  me feel comfortable. Now that I'm a student here, I love the
                  challenging classes and all the extracurricular activities
                  available.
                </p>
                <div className="flex items-center" data-id="element-2755">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tXB2gESgQyPW5hZWnwiFvU/WhatsApp_Image_2025-08-08_at_06.35.51_%281%29.jpg" alt="Student" className="w-12 h-12 rounded-full object-cover mr-4" data-id="element-2756" />
                  <div data-id="element-2757">
                    <h4 className="font-bold text-blue-900" data-id="element-2758">Chioma Nwosu</h4>
                    <p className="text-sm text-gray-500" data-id="element-2759">
                      Current JSS2 Student
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>;
};
export default Admissions;